import React, { useState, useEffect } from 'react';
import { X, Send, AlertCircle } from 'lucide-react';
import { API_URL } from '../../config';

export default function SnapTransferModal({ availableSnaps, onClose, onSuccess }) {
  const [recipientEmail, setRecipientEmail] = useState('');
  
  // Body scroll lock when modal is open
  useEffect(() => {
    const originalStyle = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${window.scrollY}px`;
    
    return () => {
      const scrollY = document.body.style.top;
      document.body.style.overflow = originalStyle;
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    };
  }, []);
  const [amount, setAmount] = useState(Number(availableSnaps.toFixed(2)));  // Formatiert
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');

  const handleTransfer = async () => {
    setError('');
    
    if (!recipientEmail?.trim()) {
      setError('Bitte Email-Adresse eingeben');
      return;
    }
    
    if (amount <= 0 || amount > availableSnaps) {
      setError(`Ungültige Menge. Du hast ${availableSnaps.toFixed(2)} SNAPs verfügbar.`);
      return;
    }

    setIsSending(true);

    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/snaps/transfer`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          recipient_email: recipientEmail,
          amount: amount
        })
      });

      if (!res.ok) {
        const data = await res.json();
        // Handle various error formats
        let errorMsg = 'Transfer fehlgeschlagen';
        if (typeof data.detail === 'string') {
          errorMsg = data.detail;
        } else if (Array.isArray(data.detail)) {
          errorMsg = data.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (data.detail?.msg) {
          errorMsg = data.detail.msg;
        }
        throw new Error(errorMsg);
      }

      const data = await res.json();
      
      alert(`✅ Transfer erfolgreich!\n\n${amount} SNAPs wurden an ${recipientEmail} gesendet.\n\nNeues Guthaben: ${Number(data.new_balance).toFixed(2)} SNAPs`);
      
      if (onSuccess) onSuccess();
      onClose();
      
    } catch (error) {
      console.error('Transfer error:', error);
      setError(error.message || 'Transfer fehlgeschlagen');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-black border-2 border-[#10B981]/30 rounded-2xl p-6 sm:p-8 glow-green-soft">
        
        {/* Close Button */}
        <button
          onClick={onClose}
          disabled={isSending}
          className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-lg transition-all"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center glow-green">
            <Send className="w-8 h-8 text-black" />
          </div>
          <h3 className="text-2xl font-bold mb-2">SNAPs verschenken</h3>
          <p className="text-white/70 text-sm">
            Sende deine SNAPs an einen Freund mit aktivem Avatar
          </p>
        </div>

        {/* Form */}
        <div className="space-y-4 mb-6">
          
          {/* Available SNAPs */}
          <div className="p-3 bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl text-center">
            <p className="text-xs text-white/70 mb-1">Verfügbare SNAPs</p>
            <p className="text-2xl font-bold text-[#10B981]">{Number(availableSnaps.toFixed(2))}</p>
          </div>

          {/* Recipient Email */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Empfänger Email-Adresse
            </label>
            <input
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="freund@example.com"
              className="w-full px-4 py-3 bg-black border-2 border-white/30 rounded-xl text-white placeholder:text-white/40 focus:border-[#10B981] focus:ring-2 focus:ring-[#10B981]/50 transition-all"
              disabled={isSending}
            />
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Anzahl SNAPs
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => {
                // Erlaube Komma und Punkt als Dezimaltrenner
                const value = e.target.value.replace(',', '.');
                const parsed = parseFloat(value);
                setAmount(isNaN(parsed) ? 0 : parsed);
              }}
              className="w-full px-4 py-3 bg-black border-2 border-white/30 rounded-xl text-white focus:border-[#10B981] focus:ring-2 focus:ring-[#10B981]/50 transition-all"
              disabled={isSending}
            />
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-500">{error}</p>
            </div>
          )}

          {/* Info */}
          <div className="p-3 bg-white/5 border border-white/10 rounded-xl">
            <p className="text-xs text-white/70 leading-relaxed">
              <span className="font-bold">Hinweis:</span> SNAPs können nur an User mit <span className="font-bold text-[#10B981]">aktivem Avatar</span> gesendet werden. 
              Der Transfer ist sofort und kann nicht rückgängig gemacht werden.
            </p>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isSending}
            className="flex-1 py-3 px-6 border-2 border-white/30 text-white rounded-xl hover:bg-white/5 transition-all font-medium disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            onClick={handleTransfer}
            disabled={isSending || !recipientEmail?.trim() || amount <= 0}
            className="flex-1 py-3 px-6 bg-[#10B981] text-black rounded-xl hover:bg-[#059669] transition-all font-bold glow-green pulse-glow disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSending ? 'Sende...' : `${Number(amount.toFixed(2))} SNAPs senden`}
          </button>
        </div>
      </div>
    </div>
  );
}
